<script setup>
// NOTICE: KAPTION-INTEGRATION
// Step 2: Template Selection + Parameter Binding with enhanced preview
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import useTemplateVariables from 'dashboard/composables/useTemplateVariables';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import KaptionMediaUpload from 'dashboard/components-next/Campaigns/KaptionMediaUpload.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import { useKaptionBusinessName } from 'dashboard/composables/useKaptionBusinessName';
import Spinner from 'shared/components/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { MEDIA_FORMATS } from 'dashboard/helper/templateHelper';
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: FLOW button prefill / status
import KaptionFlowTemplateSection from 'dashboard/components-next/Campaigns/KaptionFlowTemplateSection.vue';
import {
  WHATSAPP_FLOWS_FLAG,
  templateHasFlowButton,
} from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    default: null,
  },
  templateId: {
    type: [Number, String],
    default: null,
  },
  selectedAudience: {
    type: Array,
    default: () => [],
  },
  variableBindings: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'update:templateId',
  'update:variableBindings',
  'update:allVariablesMapped',
]);

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

// Internal state
const internalTemplateId = computed({
  get: () => props.templateId,
  set: value => emit('update:templateId', value),
});

const sampleContacts = ref([]);
const isLoadingContacts = ref(false);
const selectedContactId = ref(null);

// Preview mode toggle: 'link' (edit mode) or 'show' (bind mode)
const previewMode = ref('link');
const iframeMode = computed(() =>
  previewMode.value === 'link' ? 'edit' : 'bind'
);

// Tab configuration for mode toggle
const modeTabs = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.LINK_TAB'),
    value: 'link',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.SHOW_TAB'),
    value: 'show',
  },
]);
const activeTabIndex = computed(() => (previewMode.value === 'link' ? 0 : 1));
const handleModeTabChange = tab => {
  previewMode.value = tab.value;
};

// Store getters
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const labels = useMapGetter('labels/getLabels');
const contactSegments = useMapGetter('customViews/getContactCustomViews');

// Options
// NOTICE: KAPTION-INTEGRATION - Filter out internal kaption_ templates.
// `kaption_*` is reserved for system-managed templates (reminders,
// scheduled messages, follow-ups). Customer-managed templates (any other
// name) flow through here normally; the backend additionally restricts
// them by category at send time.
const templateOptions = computed(() => {
  if (!props.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(props.inboxId);
  return templates
    .filter(template => !template.name.startsWith('kaption_'))
    .map(template => {
      const friendlyName = template.name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());

      return {
        value: template.id,
        label: `${friendlyName} (${template.language || 'en'})`,
        template: template,
      };
    });
});

// Template data
const selectedTemplate = computed(() => {
  if (!props.inboxId || !internalTemplateId.value) return null;
  const templates = getFilteredWhatsAppTemplates.value(props.inboxId);
  return templates.find(template => template.id === internalTemplateId.value);
});

// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the preview header names the business.
const previewBusinessName = useKaptionBusinessName(
  computed(() => props.inboxId)
);

const templateText = computed(() => {
  if (!selectedTemplate.value) return '';
  const bodyComponent = selectedTemplate.value.components.find(
    c => c.type === 'BODY'
  );
  return bodyComponent ? bodyComponent.text : '';
});

// Detect media header (IMAGE, VIDEO, DOCUMENT)
const headerComponent = computed(() => {
  if (!selectedTemplate.value) return null;
  return selectedTemplate.value.components.find(c => c.type === 'HEADER');
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const mediaHeaderType = computed(
  () => headerComponent.value?.format?.toLowerCase() || ''
);

// Extract template's example image URL (used as default)
const templateExampleMediaUrl = computed(() => {
  const hdr = headerComponent.value;
  if (!hdr) return '';
  return (
    hdr.example?.header_handle?.[0] ||
    hdr.example?.header_url?.[0] ||
    hdr.example?.[0] ||
    ''
  );
});

// Template clone with overridden header media URL for the preview.
// The store's selectedTemplate has the original example URL; when the user
// uploads a different image we need the preview to reflect it.
const previewTemplate = computed(() => {
  if (!selectedTemplate.value) return null;

  const overrideUrl = props.variableBindings?.header?.media_url;
  if (!overrideUrl || !hasMediaHeader.value) return selectedTemplate.value;

  // Shallow-clone components, replacing only the HEADER's example URL
  const components = selectedTemplate.value.components.map(c => {
    if (c.type !== 'HEADER') return c;
    return {
      ...c,
      example: { ...c.example, header_handle: [overrideUrl] },
    };
  });

  return { ...selectedTemplate.value, components };
});

// Header media URL update handler
const updateHeaderMediaUrl = value => {
  const newBindings = {
    ...props.variableBindings,
    header: {
      media_url: value,
      media_type: mediaHeaderType.value,
    },
  };
  emit('update:variableBindings', newBindings);
};

// Extract variables from template
const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;

const templateVariables = computed(() => {
  if (!templateText.value) return [];
  const matches = Array.from(templateText.value.matchAll(VARIABLE_PATTERN));
  const keys = matches.map(match => match[1].trim());
  return [...new Set(keys)];
});

// Variable suggestions for the editor
const { variableSuggestions } = useTemplateVariables({
  sampleContacts: computed(() => sampleContacts.value),
});

// Convert variable suggestions to the format expected by the iframe
const availableBindingsForSlot = computed(() => {
  const suggestions = variableSuggestions.value || [];
  return suggestions
    .filter(s => s.key?.startsWith('contact.'))
    .map(s => ({
      value: `{{ ${s.key} }}`,
      label: s.label,
    }));
});

// Build variables array for the iframe in the expected format
const iframeVariables = computed(() => {
  const bindings = props.variableBindings?.body || {};

  return templateVariables.value.map(slot => ({
    slot,
    binding: bindings[slot] || null,
    availableBindings: availableBindingsForSlot.value,
  }));
});

// Build resolved values for preview
const selectedContact = computed(() => {
  if (!selectedContactId.value) return null;
  return sampleContacts.value.find(c => c.id === selectedContactId.value);
});

const resolvedValues = computed(() => {
  const contact = selectedContact.value;
  if (!contact) return {};

  const values = {};

  // Map common contact fields
  values['{{ contact.name }}'] = contact.name || '';
  values['{{ contact.first_name }}'] =
    contact.name?.split(' ')[0] || contact.name || '';
  values['{{ contact.last_name }}'] =
    contact.name?.split(' ').slice(1).join(' ') || '';
  values['{{ contact.email }}'] = contact.email || '';
  values['{{ contact.phone_number }}'] = contact.phone_number || '';

  // Add custom attributes
  if (contact.custom_attributes) {
    Object.entries(contact.custom_attributes).forEach(([key, val]) => {
      values[`{{ contact.custom_attribute.${key} }}`] = String(val);
    });
  }

  return values;
});

// Helper functions for contact display
const getInitials = name => {
  if (!name) return 'C';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getDisplayName = contact => {
  return contact?.name || contact?.email || contact?.phone_number || 'Contact';
};

const getContactSubtitle = contact => {
  if (contact?.phone_number) return contact.phone_number;
  if (contact?.email) return contact.email;
  return '';
};

const selectContact = contactId => {
  selectedContactId.value = contactId;
};

// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the FLOW button section
// writes variableBindings.flow ({flowActionData, kaptionFlowId, blocked});
// the dialog turns it into processed_params.buttons and refuses blocked flows.
const showFlowSection = computed(
  () =>
    isKaptionFlagEnabled(WHATSAPP_FLOWS_FLAG) &&
    templateHasFlowButton(selectedTemplate.value)
);

const updateFlowBinding = flow => {
  emit('update:variableBindings', { ...props.variableBindings, flow });
};

// Check if all variables are mapped
const checkAllVariablesMapped = (bindings = null) => {
  const bindingsToCheck = bindings ?? props.variableBindings;

  if (templateVariables.value.length === 0) {
    emit('update:allVariablesMapped', true);
    return;
  }

  const variableBindings = bindingsToCheck?.body || {};
  const allMapped = templateVariables.value.every(varKey => {
    return variableBindings[varKey] && variableBindings[varKey].trim() !== '';
  });

  emit('update:allVariablesMapped', allMapped);
};

// Handle binding updates from the iframe
const handleBindingsUpdate = bindings => {
  // Convert from iframe format to our format
  const newBody = {};
  bindings.forEach(({ slot, binding }) => {
    if (binding) {
      newBody[slot] = binding;
    }
  });

  const newBindings = { ...props.variableBindings, body: newBody };
  emit('update:variableBindings', newBindings);
  checkAllVariablesMapped(newBindings);
};

// Fetch sample contacts
const fetchSampleContacts = async () => {
  if (!props.selectedAudience || props.selectedAudience.length === 0) {
    sampleContacts.value = [];
    return;
  }

  isLoadingContacts.value = true;
  try {
    const audienceRequests = props.selectedAudience.map(entry => {
      if (typeof entry === 'number' || typeof entry === 'string') {
        const label = labels.value.find(l => l.id === entry);
        return label
          ? ContactAPI.get(1, 'created_at', label.title).then(
              response => response.data?.payload || []
            )
          : Promise.resolve([]);
      }

      if (entry?.type === 'Label') {
        const label = labels.value.find(l => l.id === entry.id);
        return label
          ? ContactAPI.get(1, 'created_at', label.title).then(
              response => response.data?.payload || []
            )
          : Promise.resolve([]);
      }

      if (entry?.type === 'Segment') {
        const segment = contactSegments.value?.find(s => s.id === entry.id);
        return segment?.query
          ? ContactAPI.filter(1, 'created_at', segment.query).then(
              response => response.data?.payload || []
            )
          : Promise.resolve([]);
      }

      return Promise.resolve([]);
    });

    const allContacts = (await Promise.all(audienceRequests)).flat();

    const seen = new Set();
    sampleContacts.value = allContacts.filter(contact => {
      if (seen.has(contact.id)) {
        return false;
      }

      seen.add(contact.id);
      return true;
    });

    sampleContacts.value = sampleContacts.value.slice(0, 5);

    if (sampleContacts.value.length > 0) {
      selectedContactId.value = sampleContacts.value[0].id;
    }
  } catch (error) {
    // Silently fail
  } finally {
    isLoadingContacts.value = false;
  }
};

// Watchers
watch(
  () => props.selectedAudience,
  () => {
    fetchSampleContacts();
  },
  { deep: true, immediate: true }
);

watch(internalTemplateId, newId => {
  // Reset bindings when template changes, pre-fill header from template example
  const newBindings = {};

  if (newId && hasMediaHeader.value && templateExampleMediaUrl.value) {
    newBindings.header = {
      media_url: templateExampleMediaUrl.value,
      media_type: mediaHeaderType.value,
    };
  }

  emit('update:variableBindings', newBindings);
  checkAllVariablesMapped();
});

watch(
  () => props.variableBindings,
  () => {
    checkAllVariablesMapped();
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="grid grid-cols-5 gap-6 h-full overflow-visible">
    <!-- Left Column: Template Selection (3/5 width) -->
    <div class="col-span-3 flex flex-col gap-6 overflow-visible pr-2">
      <!-- Template Selection -->
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
        </label>
        <ComboBox
          v-model="internalTemplateId"
          :options="templateOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
        </p>
        <!-- NOTICE: KAPTION-INTEGRATION - Link to create new template -->
        <router-link
          v-if="isKaptionFlagEnabled('KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT')"
          :to="{ name: 'campaigns_whatsapp_templates_new' }"
          target="_blank"
          class="inline-flex items-center gap-1 text-xs text-n-brand hover:text-n-brand-hover mt-1"
        >
          <span class="i-lucide-plus w-3 h-3" />
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.CREATE_NEW') }}
        </router-link>
      </div>

      <!-- Header Media Upload (for IMAGE/VIDEO/DOCUMENT templates) -->
      <div
        v-if="hasMediaHeader && selectedTemplate"
        class="flex flex-col gap-1"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.HEADER_FORMAT_LABEL', {
              format:
                mediaHeaderType.charAt(0).toUpperCase() +
                mediaHeaderType.slice(1),
            })
          }}
        </label>
        <KaptionMediaUpload
          :model-value="variableBindings?.header?.media_url || ''"
          :media-type="mediaHeaderType"
          @update:model-value="updateHeaderMediaUrl"
        />
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-flows -->
      <KaptionFlowTemplateSection
        v-if="showFlowSection"
        :inbox-id="inboxId"
        :template="selectedTemplate"
        :model-value="variableBindings?.flow || {}"
        @update:model-value="updateFlowBinding"
      />

      <!-- Sample Recipients List for Preview -->
      <div
        v-if="sampleContacts.length > 0 && templateText"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.PREVIEW_CONTACT') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.CONTACTS_COUNT', {
                count: sampleContacts.length,
              })
            }}
          </span>
        </div>

        <!-- Recipients List - Same style as Step 3 -->
        <div class="flex flex-col gap-1">
          <button
            v-for="contact in sampleContacts"
            :key="contact.id"
            type="button"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left"
            :class="[
              selectedContactId === contact.id
                ? 'bg-n-alpha-brand1'
                : 'hover:bg-n-alpha-black2',
            ]"
            @click="selectContact(contact.id)"
          >
            <!-- Avatar -->
            <div
              class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
              :class="[
                selectedContactId === contact.id
                  ? 'bg-n-brand text-white'
                  : 'bg-n-slate-3 text-n-slate-11',
              ]"
            >
              {{ getInitials(contact.name) }}
            </div>

            <!-- Contact Info -->
            <div class="flex flex-col min-w-0 flex-1">
              <span
                class="text-sm font-medium truncate"
                :class="[
                  selectedContactId === contact.id
                    ? 'text-n-brand'
                    : 'text-n-slate-12',
                ]"
              >
                {{ getDisplayName(contact) }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                {{ getContactSubtitle(contact) }}
              </span>
            </div>

            <!-- Selected Indicator -->
            <span
              v-if="selectedContactId === contact.id"
              class="i-lucide-check w-5 h-5 text-n-brand flex-shrink-0"
            />
          </button>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.CLICK_CONTACT_INFO') }}
        </p>
      </div>

      <!-- Loading state -->
      <div
        v-if="isLoadingContacts"
        class="flex items-center justify-center h-32"
      >
        <Spinner size="large" />
      </div>
    </div>

    <!-- Right Column: Preview with Mode Toggle (2/5 width) -->
    <div class="col-span-2 flex flex-col gap-3">
      <!-- Preview Header with Mode Toggle -->
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.MESSAGE_PREVIEW') }}
        </h3>

        <!-- Link / Show Toggle - NOTICE: KAPTION-INTEGRATION -->
        <TabBar
          v-if="templateText"
          :tabs="modeTabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleModeTabChange"
        />
      </div>

      <!-- Mode Description -->
      <p v-if="templateText" class="text-xs text-n-slate-11 -mt-1">
        <template v-if="previewMode === 'link'">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.LINK_MODE_DESCRIPTION')
          }}
        </template>
        <template v-else>
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.SHOW_MODE_DESCRIPTION')
          }}
        </template>
      </p>
      <!-- eslint-enable vue/no-bare-strings-in-template -->

      <!-- Preview Area -->
      <div
        v-if="sampleContacts.length > 0 && templateText"
        class="flex flex-col"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - Enhanced WhatsApp Preview with edit/bind modes -->
        <KaptionWhatsAppPreviewEnhanced
          :template="previewTemplate"
          :variables="iframeVariables"
          :resolved-values="resolvedValues"
          :mode="iframeMode"
          :loading="isLoadingContacts"
          :bare="false"
          :company-name="previewBusinessName"
          class="w-full h-[520px]"
          @bindings-update="handleBindingsUpdate"
        />
      </div>

      <div
        v-else-if="!templateText"
        class="flex flex-col items-center justify-center h-full"
      >
        <KaptionWhatsAppPreviewEnhanced
          :template="null"
          :loading="false"
          :bare="false"
          :company-name="previewBusinessName"
          mode="render"
          class="w-full h-[520px]"
        />
      </div>

      <div
        v-else-if="isLoadingContacts"
        class="flex flex-col items-center justify-center h-full"
      >
        <KaptionWhatsAppPreviewEnhanced
          :template="previewTemplate"
          loading
          :bare="false"
          :company-name="previewBusinessName"
          mode="render"
          class="w-full h-[520px]"
        />
      </div>

      <div
        v-else
        class="flex items-center justify-center h-full text-n-ruby-11 text-sm"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_2.NO_CONTACTS_PREVIEW') }}
      </div>
    </div>
  </div>
</template>
